import { useCallback, useEffect, useRef, useState } from "react";
import { FaUpload } from "react-icons/fa"; // upload icon
import { fetchConversationAppointment, fetchMessagesWith, getCurrentUserIdFromToken, sendAppointment, sendMessage } from "./Api";
import useWebSocket from "./messages/WebSockets";

const formatTime = (time) => new Date(time).toLocaleString();

const MessageModal = ({ user, onClose }) => {
  const currentUserId = getCurrentUserIdFromToken();
  const chatBoxRef = useRef(null);
  const messageInputRef = useRef(null);

  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [conversationId, setConversationId] = useState(null);

  const [showCalendarDock, setShowCalendarDock] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [middleAppointment, setMiddleAppointment] = useState(null);

  // --- Load messages (page-based) ---
  const loadMessages = useCallback(async (pageNumber = 1, prepend = false) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchMessagesWith(user.id, pageNumber);

      if (data.messages.length > 0 && !conversationId) {
        setConversationId(data.messages[0].conversation_id);
      }

      const transformed = data.messages.map(msg => ({
        id: msg.id,
        text: msg.content,
        from: msg.sender_id === currentUserId ? "me" : "them",
        sentAt: msg.sent_at,
      }));

      setChatHistory(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMessages = transformed.filter(m => !existingIds.has(m.id));
        return prepend ? [...newMessages, ...prev] : [...prev, ...newMessages];
      });
      setPage(data.current_page);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [user.id, currentUserId, conversationId]);

  // --- Initial load ---
  useEffect(() => {
    if (user?.id) {
      loadMessages(1);
    }
  }, [user, loadMessages]);

  // --- Scroll to bottom on first load ---
  useEffect(() => {
    if (chatBoxRef.current && page === 1) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatHistory, page]);

  // --- Handle WebSocket messages ---
  const handleIncomingMessage = useCallback((data) => {
    try {
      const msg = typeof data === "string" ? JSON.parse(data) : data;
      if (msg.sender_id === user.id || msg.recipient_id === user.id) {
        setChatHistory(prev => [
          ...prev,
          {
            id: msg.id,
            text: msg.content,
            from: msg.sender_id === currentUserId ? "me" : "them",
            sentAt: msg.sent_at,
          }
        ]);

        // Scroll to bottom on new message from self or other
        // Auto-scroll after the message is added
        setTimeout(scrollToBottom, 50); // short delay to ensure DOM updated
      }
    } catch (err) {
      console.error("Failed to parse WebSocket message:", data);
    }
  }, [user.id, currentUserId]);

  useWebSocket(handleIncomingMessage);

  // --- Send message ---
  const handleSend = () => {
    if (!message.trim()) return;

    const tempId = Date.now();
    setChatHistory(prev => [...prev, { id: tempId, text: message, from: "me", sentAt: new Date().toISOString(), pending: true }]);

    console.log("Sending message:", message); // <--- debug here

    sendMessage(user.id, message)
      .then(sent => {
        setChatHistory(prev => prev.map(msg => msg.id === tempId ? { ...msg, id: sent.id, sentAt: sent.sent_at, pending: false } : msg));
      })
      .catch(err => {
        setChatHistory(prev => prev.map(msg => msg.id === tempId ? { ...msg, pending: false, failed: true } : msg));
        setError(err.message || "Failed to send message");
      });

    setMessage(""); // reset after sending
    messageInputRef.current?.focus();
    scrollToBottom();
  };
const getPHNow = () => {
  const now = new Date();

  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now).replace(/\//g, "-");

  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Manila",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);

  return { date, time };
};

const { date: todayDate, time: currentTime } = getPHNow();

  // --- Send appointment ---
 const handleSendAppointment = async () => {
  if (!scheduleName || !appointmentDate || !appointmentTime) {
    alert("Please fill out all appointment fields.");
    return;
  }

  const selectedDateTime = new Date(`${appointmentDate}T${appointmentTime}:00`);
  const now = new Date();

  if (selectedDateTime < now) {
    alert("Cannot set an appointment in the past.");
    return;
  }

  try {
    await sendAppointment({
      type: "conversation",
      target_id: conversationId,
      schedule_name: scheduleName,
      date: appointmentDate,
      hour: appointmentTime + ":00",
    });

    setMiddleAppointment({
      schedule_name: scheduleName,
      date: appointmentDate,
      hour: appointmentTime + ":00",
    });

    setShowCalendarDock(false);
    setScheduleName("");
    setAppointmentDate("");
    setAppointmentTime("");
  } catch (err) {
    alert(err.message || "Failed to send appointment");
  }
};

  const handleToggleCalendar = async () => {
    const nextState = !showCalendarDock;
    setShowCalendarDock(nextState);

    if (nextState) {
      try {
        const appointments = await fetchConversationAppointment(conversationId);
        if (appointments && appointments.length > 0) {
          const { schedule_name, date, hour } = appointments[0];
          setScheduleName(schedule_name || "");
          setAppointmentDate(date || "");
          setAppointmentTime(hour ? hour.split(":").slice(0, 2).join(":") : "");
        } else {
          setScheduleName("");
          setAppointmentDate("");
          setAppointmentTime("");
        }
      } catch (err) {
        console.error("Failed to fetch appointments:", err);
        setScheduleName("");
        setAppointmentDate("");
        setAppointmentTime("");
      }
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  // --- Infinite scroll handler ---
  const handleScroll = async (e) => {
    const container = e.currentTarget;
    const threshold = 20; // px from top
    if (container.scrollTop <= threshold && page < totalPages && !loading) {
      const prevHeight = container.scrollHeight;

      // Load older messages
      await loadMessages(page + 1, true);

      // Calculate new scrollTop based on difference in scrollHeight
      requestAnimationFrame(() => {
        const newHeight = container.scrollHeight;
        container.scrollTop = newHeight - prevHeight + container.scrollTop;
      });
    }
  };
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth" // smooth scroll
      });
    }
  };


  return (
    <div
      className="nested-modal"
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "100%",
        maxWidth: "500px",
        height: "600px",       // <-- fixed height
        backgroundColor: "#a56c50",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 1020
      }}
      onMouseDown={stopPropagation}
    >

      {/* Header */}
      <div style={{ position: "relative", 
        padding: "12px",
         //borderBottom: "1px solid #ff9b45",
          display: "flex",
           justifyContent: "space-between", 
          alignItems: "center", 
        backgroundColor: "#682c19", 
          borderTopLeftRadius: "10px",
           borderTopRightRadius: "10px",
          //   color: "#fff" 
            }}>
        <strong>{user.full_name || user.name}</strong>
        <button onClick={onClose} style={{ position: "absolute", top: "50%", right: "12px", transform: "translateY(-50%)", background: "transparent", border: "none", fontSize: "28px", cursor: "pointer", padding: "6px" }} aria-label="Close chat">❌</button>
      </div>

      {/* Chat Box */}
      <div ref={chatBoxRef} onScroll={handleScroll} style={{ flex: 1, overflowY: "scroll", padding: "10px", display: "flex", flexDirection: "column", gap: "12px", scrollbarWidth: "thin", scrollbarColor: "#888 #f1f1f1" }}>
        {



          chatHistory.map((msg, idx) => (
            <div
              key={msg.id ? `${msg.id}-${msg.sentAt}-${idx}` : `temp-${Date.now()}-${idx}`}
              style={{
                display: "flex",
                flexDirection: msg.from === "me" ? "column-reverse" : "column",
                alignItems: msg.from === "me" ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={{
                  backgroundColor: msg.from === "me" ? "#d96830" : "#e0e0e0",
                  color: msg.from === "me" ? "#fff" : "#000",
                  padding: "10px 14px",
                  borderRadius: "16px",
                  maxWidth: "80%",
                  wordBreak: "break-word",
                  fontSize: "14px",
                  lineHeight: "1.4",
                  //boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                }}
              >
                {msg.text}
              </div>
              <div style={{ fontSize: "11px", marginTop: "4px", color: "#FFFAF0", fontStyle: "italic" }}>
                {formatTime(msg.sentAt)}
              </div>
            </div>
          ))




        }
      </div>


      {/* Input & calendar */}
      <div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid #ddd", padding: "8px", gap: "8px" }}>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>


          {showCalendarDock && (
            <div style={{ backgroundColor: "#f3fb3f3", padding: "8px", borderRadius: "6px", display: "flex", gap: "8px", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "0" }}>
                <div style={{ display: "flex", gap: "0" }}>
                  <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "0" }}>
                    <label style={{ fontSize: "12px", marginRight: "2px" }}>Date</label>
<input
  type="date"
  min={getPHNow().date}
  value={appointmentDate}
  onChange={e => setAppointmentDate(e.target.value)}
  style={{
    flex: 1,
    padding: "4px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "12px",
    cursor: "pointer"
  }}
  onClick={e => e.target.showPicker?.()}
/>                 </div>
                  <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "0", marginLeft: "4px" }}>
                    <label style={{ fontSize: "12px", marginRight: "2px" }}>Hour</label>
<input
  type="time"
  min={appointmentDate === todayDate ? currentTime : undefined}
  value={appointmentTime}
onChange={e => {
  const selectedTime = e.target.value;

  if (
    appointmentDate === todayDate &&
    selectedTime < currentTime
  ) {
    alert("Cannot select a past time");
    return;
  }

  setAppointmentTime(selectedTime);
}}  style={{
    flex: 1,
    padding: "4px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "12px",
    cursor: "pointer"
  }}
  onClick={e => e.target.showPicker?.()}
  
/>                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0", marginTop: "0" }}>
                  <label style={{ fontSize: "12px", marginRight: "2px" }}>Schedule Name</label>
                  <input type="text" placeholder="Title" value={scheduleName} onChange={e => setScheduleName(e.target.value)} style={{ flex: 1, padding: "4px", borderRadius: "4px", border: "1px solid #b4b4b4", fontSize: "12px", fontFamily: "arial",}} />
                </div>
              </div>
              <button onClick={handleSendAppointment} style={{ border: "none", borderRadius: "6px", padding: "10px 14px", fontSize: "30px", cursor: "pointer", color: "#050505ff", alignSelf: "center", display: "flex", justifyContent: "center", alignItems: "center" }}> <FaUpload /></button>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

            {user.friendship_status === "accepted" && (

              <button onClick={handleToggleCalendar} style={{ backgroundColor: "#e0e0e0", border: "none", borderRadius: "50%", width: "36px", height: "36px", fontSize: "18px", cursor: "pointer" }}>📅</button>


            )}
            <input ref={messageInputRef} type="text" placeholder="Type a message..." value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSend()} style={{ flex: 1, padding: "10px", border: "1px solid #ccc", borderRadius: "20px", fontSize: "14px" }} />
            <button onClick={handleSend} style={{ backgroundColor: "#007bff", color: "#fff", border: "none", borderRadius: "20px", padding: "8px 16px", cursor: "pointer" }}>Send</button>
          </div>



        </div>




      </div>
    </div>
  );
};

export default MessageModal;
